import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Google,
  Facebook,
} from '@mui/icons-material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { signup, googleAuth, facebookAuth } from '../../store/authSlice';
import { showToast } from '../../store/uiSlice';
import Logo from '../../assets/header-logo.svg';

// Declare Facebook SDK type
declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

const Signup: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  // Check if OAuth credentials are configured
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID || '';
  const isGoogleConfigured =
    googleClientId &&
    !googleClientId.includes('your-google-client-id') &&
    googleClientId.includes('.apps.googleusercontent.com');
  const isFacebookConfigured =
    facebookAppId && !facebookAppId.includes('your-facebook-app-id');

  // Initialize Facebook SDK
  useEffect(() => {
    if (!isFacebookConfigured) return;

    if (!window.FB) {
      window.fbAsyncInit = function () {
        window.FB.init({
          appId: facebookAppId,
          cookie: true,
          xfbml: true,
          version: 'v18.0',
        });
      };

      (function (d, s, id) {
        var js,
          fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s) as HTMLScriptElement;
        js.id = id;
        js.src = 'https://connect.facebook.net/en_US/sdk.js';
        fjs.parentNode?.insertBefore(js, fjs);
      })(document, 'script', 'facebook-jssdk');
    }
  }, [isFacebookConfigured, facebookAppId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dispatch(signup({ name, email, password })).unwrap();
      dispatch(showToast({ message: 'Account created! Welcome to Agile Edge.', type: 'success' }));
      navigate('/workspaces');
    } catch (err: any) {
      const errorMessage = err?.message || 'Signup failed. Please try again.';
      dispatch(showToast({ message: errorMessage, type: 'error' }));
      console.error('Signup failed:', err);
    }
  };

  // Google Login Handler
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        await dispatch(
          googleAuth({
            credential: tokenResponse.access_token,
          })
        ).unwrap();
        navigate('/dashboard');
      } catch (err) {
        console.error('Google signup failed:', err);
      }
    },
    onError: () => {
      console.error('Google signup failed');
    },
  });

  const handleGoogleLogin = () => {
    if (!isGoogleConfigured) {
      alert(
        'Google OAuth is not configured. Please add your Google Client ID to the .env file.\n\nSee OAUTH_SETUP.md for detailed setup instructions.'
      );
      return;
    }
    googleLogin();
  };

  // Facebook Login Handler
  const handleFacebookLogin = () => {
    if (!isFacebookConfigured) {
      alert(
        'Facebook OAuth is not configured. Please add your Facebook App ID to the .env file.\n\nSee OAUTH_SETUP.md for detailed setup instructions.'
      );
      return;
    }
    if (window.FB) {
      window.FB.login(
        (response: any) => {
          if (response.authResponse) {
            dispatch(
              facebookAuth({
                accessToken: response.authResponse.accessToken,
                userID: response.authResponse.userID,
              })
            )
              .unwrap()
              .then(() => {
                navigate('/dashboard');
              })
              .catch((err) => {
                console.error('Facebook signup failed:', err);
              });
          } else {
            console.error('Facebook signup cancelled');
          }
        },
        { scope: 'public_profile,email' }
      );
    } else {
      console.error('Facebook SDK not loaded');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(circle at 20% 50%, rgba(97, 97, 255, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0, 115, 234, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', py: 4, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            borderRadius: 4,
            overflow: 'hidden',
            backgroundColor: 'white',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Left Side - Branding */}
          <Box
            sx={{
              flex: { xs: 'none', md: 1 },
              background: 'linear-gradient(135deg, #4A90E2 0%, #5AB9EA 100%)',
              p: { xs: 6, md: 8 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: { xs: '300px', md: 'auto' },
              '&::before': {
                content: '""',
                position: 'absolute',
                width: '400px',
                height: '400px',
                background:
                  'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
                top: '-150px',
                right: '-150px',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {/* Logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
                <Box
                  component="img"
                  src={Logo}
                  alt="Agile Edge Logo"
                  sx={{
                    height: 56,
                    width: 'auto',
                    filter: 'brightness(0) invert(1)', // Makes the logo white
                  }}
                />
                <Typography variant="h4" fontWeight="700" sx={{ textTransform: 'lowercase' }}>
                  agile edge
                </Typography>
              </Box>

              {/* Welcome Text */}
              <Typography variant="h3" fontWeight="700" sx={{ mb: 2 }}>
                Join thousands of teams
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}>
                Get started with Agile Edge today and transform the way your team works
              </Typography>

              {/* Feature List */}
              <Stack spacing={2}>
                {[
                  'Free forever for up to 2 users',
                  'No credit card required',
                  'Set up in minutes',
                ].map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </Box>
                    <Typography variant="body1">{feature}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* Right Side - Signup Form */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 4, md: 8 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              maxWidth: { xs: '100%', md: '500px' },
              mx: 'auto',
            }}
          >
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" fontWeight="700" color="text.primary" sx={{ mb: 1 }}>
                Create your account
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Get started - it's free. No credit card needed.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Full Name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                required
                autoComplete="name"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Work Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
                autoComplete="email"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Choose a password (8+ characters)"
                required
                autoComplete="new-password"
                inputProps={{ minLength: 8 }}
                helperText="Use 8 or more characters with letters, numbers & symbols"
                sx={{ mb: 4 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  mb: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #4A90E2 0%, #5AB9EA 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #357ABD 0%, #4AA3D5 100%)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Get Started'}
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 3 }}>
                By signing up, you agree to our{' '}
                <Link to="/terms" style={{ color: '#4A90E2', textDecoration: 'none' }}>
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link to="/privacy" style={{ color: '#4A90E2', textDecoration: 'none' }}>
                  Privacy Policy
                </Link>
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            <Stack spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleGoogleLogin}
                startIcon={<Google />}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  borderColor: '#dadce0',
                  color: '#3c4043',
                  '&:hover': {
                    borderColor: '#d2d3d4',
                    backgroundColor: '#f8f9fa',
                  },
                }}
              >
                Continue with Google
              </Button>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleFacebookLogin}
                startIcon={<Facebook />}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  borderColor: '#dadce0',
                  color: '#3c4043',
                  '&:hover': {
                    borderColor: '#d2d3d4',
                    backgroundColor: '#f8f9fa',
                  },
                }}
              >
                Continue with Facebook
              </Button>
            </Stack>

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link
                  to="/"
                  style={{
                    color: '#4A90E2',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  Log in
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Signup;
